import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [estimatedAge, setEstimatedAge] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target?.result as string);
        setIsProcessing(true);
        
        setTimeout(() => {
          const randomAge = Math.floor(Math.random() * (35 - 20 + 1)) + 20;
          setEstimatedAge(randomAge);
          setIsProcessing(false);
        }, 2000);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Modern gradient with mesh pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-neutral-900 to-zinc-950"></div>
      
      {/* Floating elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-teal-400/20 to-cyan-400/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-32 right-20 w-96 h-96 bg-gradient-to-r from-emerald-400/15 to-teal-400/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      {/* Grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Message */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-6xl md:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">
                  {t('hero.title')}
                </span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient">
                  {t('hero.title2')}
                </span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                {t('hero.subtitle')}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <button className="group px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl font-semibold text-white shadow-lg shadow-emerald-500/50 hover:shadow-emerald-500/70 transition-all duration-300 hover:scale-105 whitespace-nowrap">
                {t('hero.cta.demo')}
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
              </button>
              <button className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl font-semibold text-white hover:bg-white/20 transition-all duration-300 whitespace-nowrap">
                {t('hero.cta.contact')}
              </button>
            </div>
          </div>

          {/* Right: Age Guess */}
          <div className="relative">
            <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap">
                {t('hero.ageGuess.badge')}
              </div>

              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                {t('hero.ageGuess.title')}
              </h3>

              <div className="space-y-6">
                {!uploadedImage ? (
                  <label className="block cursor-pointer group">
                    <div className="relative border-2 border-dashed border-gray-600 rounded-2xl p-12 text-center hover:border-emerald-500 transition-all duration-300 bg-gray-800/30">
                      <i className="ri-upload-cloud-line text-6xl text-gray-500 group-hover:text-emerald-400 transition-colors mb-4 block"></i>
                      <p className="text-gray-400 group-hover:text-emerald-400 transition-colors font-semibold">
                        {t('hero.ageGuess.upload')}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="space-y-4">
                    <div className="relative rounded-2xl overflow-hidden">
                      <img
                        src={uploadedImage}
                        alt="Uploaded"
                        className="w-full h-64 object-cover"
                      />
                    </div>

                    {isProcessing ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                        <p className="text-gray-400 mt-4">Processing...</p>
                      </div>
                    ) : estimatedAge && (
                      <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl p-6 border border-emerald-500/30">
                        <div className="text-center space-y-2">
                          <p className="text-gray-400 text-sm">{t('hero.ageGuess.result')}</p>
                          <p className="text-5xl font-bold text-emerald-400">{estimatedAge}{t('hero.ageGuess.confidence')}</p>
                          <p className="text-gray-500 text-sm">± 2{t('hero.ageGuess.confidence')}</p>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setUploadedImage(null);
                        setEstimatedAge(null);
                      }}
                      className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl text-white transition-colors whitespace-nowrap"
                    >
                      Try Another Photo
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-shield-check-line text-emerald-400"></i>
                  {t('hero.ageGuess.privacy1')}
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-cpu-line text-emerald-400"></i>
                  {t('hero.ageGuess.privacy2')}
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-off-line text-emerald-400"></i>
                  {t('hero.ageGuess.privacy3')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
